import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Package, AlertCircle } from 'lucide-react';
import zoneService from '../../services/zoneService';
import toast from 'react-hot-toast';

// All available product categories from the Product model
const AVAILABLE_CATEGORIES = [
  'Electronics',
  'Mobiles & Accessories',
  'Computers & Accessories',
  'Audio & Video',
  'Cameras & Accessories',
  'Smart Devices',
  'Fashion',
  'Clothing',
  'Men Fashion',
  'Women Fashion',
  'Kids Fashion',
  'Accessories',
  'Footwear',
  'Home',
  'Home & Living',
  'Furniture',
  'Home Decor',
  'Kitchen & Dining',
  'Bedding & Bath',
  'Beauty',
  'Beauty & Personal Care',
  'Makeup',
  'Skincare',
  'Hair Care',
  'Personal Care Appliances',
  'Grocery & Essentials',
  'Food & Beverages',
  'Fruits & Vegetables',
  'Dairy & Bakery',
  'Household Supplies',
  'Health & Wellness',
  'Medical Supplies',
  'Fitness Equipment',
  'Supplements',
  'Sports',
  'Sports & Outdoor',
  'Sports Equipment',
  'Fitness Gear',
  'Outdoor & Adventure',
  'Books',
  'Books & Education',
  'Stationery',
  'Educational Tools',
  'Toys',
  'Toys & Baby',
  'Baby Care',
  'Kids Accessories',
  'Automotive',
  'Vehicle Accessories',
  'Spare Parts',
  'Tools & Equipment',
  'Office & Industrial',
  'Office Supplies',
  'Industrial Tools',
  'Digital Products',
  'Software',
  'Subscriptions',
  'Gift Cards',
  'Others',
  'Other',
  'Handmade Products',
  'Eco-Friendly Products',
  'Customized Products',
  'Collectibles',
  'sma',
];

// Zone emoji suggestions
const ZONE_ICONS = [
  '🎁', '🛒', '💡', '🔧', '📱', '💻', '👗', '👔',
  '👟', '🏠', '🛋️', '🍳', '💄', '🧴', '🥦', '🍕',
  '🏥', '💊', '⚽', '🏋️', '📚', '✏️', '🧸', '🚗',
  '🖥️', '🎮', '🎵', '📷', '⌚', '💍', '🧢', '🎒',
];

const initialFormState = {
  name: '',
  description: '',
  icon: '📦',
  image: '',
  categories: [],
  isActive: true,
  order: 0,
};

const AdminZones = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await zoneService.getAllZonesAdmin();
      if (response.success) {
        setZones(response.data);
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      toast.error('Failed to load zones');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingZone(null);
    setForm(initialFormState);
    setShowForm(true);
  };

  const openEditForm = (zone) => {
    setEditingZone(zone);
    setForm({
      name: zone.name || '',
      description: zone.description || '',
      icon: zone.icon || '📦',
      image: zone.image || '',
      categories: zone.categories || [],
      isActive: zone.isActive !== undefined ? zone.isActive : true,
      order: zone.order || 0,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingZone(null);
    setForm(initialFormState);
  };

  const handleCategoryToggle = (category) => {
    setForm((prev) => {
      const exists = prev.categories.includes(category);
      return {
        ...prev,
        categories: exists
          ? prev.categories.filter((c) => c !== category)
          : [...prev.categories, category],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Zone name is required');
      return;
    }
    if (form.categories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }

    try {
      setSaving(true);
      if (editingZone) {
        const response = await zoneService.updateZone(editingZone._id, form);
        if (response.success) {
          toast.success('Zone updated successfully');
          closeForm();
          fetchZones();
        }
      } else {
        const response = await zoneService.createZone(form);
        if (response.success) {
          toast.success('Zone created successfully');
          closeForm();
          fetchZones();
        }
      }
    } catch (error) {
      console.error('Error saving zone:', error);
      toast.error(error.response?.data?.message || 'Failed to save zone');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`Are you sure you want to delete the zone "${zone.name}"?`)) {
      return;
    }
    try {
      const response = await zoneService.deleteZone(zone._id);
      if (response.success) {
        toast.success('Zone deleted successfully');
        fetchZones();
      }
    } catch (error) {
      console.error('Error deleting zone:', error);
      toast.error('Failed to delete zone');
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Zones Management</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Create and manage shopping zones with categories</p>
        </div>
        <button
          onClick={openCreateForm}
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-xl hover:shadow-lg transition-all text-sm font-medium flex items-center gap-2"
        >
          <Plus size={18} />
          Create Zone
        </button>
      </div>

      {/* Zone Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-10 px-3">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={closeForm} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold text-gray-800">
                {editingZone ? 'Edit Zone' : 'Create New Zone'}
              </h2>
              <button onClick={closeForm} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Zone Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Gift Zone, Electronics Zone"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of this zone"
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                />
              </div>

              {/* Icon & Order Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Icon (Emoji)</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {ZONE_ICONS.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setForm({ ...form, icon })}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg transition-all ${
                          form.icon === icon
                            ? 'bg-purple-100 ring-2 ring-purple-500 scale-110'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    placeholder="Custom emoji"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Display Order</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Lower numbers appear first</p>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
                <span className="text-sm font-medium text-gray-700">Active</span>
              </div>

              {/* Categories Multi-Select */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Select Categories * ({form.categories.length} selected)
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-2 space-y-1">
                  {AVAILABLE_CATEGORIES.map((category) => (
                    <label
                      key={category}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-all text-sm ${
                        form.categories.includes(category)
                          ? 'bg-purple-50 text-purple-800 font-medium'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.categories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                        className="rounded text-purple-600 focus:ring-purple-500"
                      />
                      {category}
                    </label>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium text-sm hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingZone ? 'Update Zone' : 'Create Zone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Zones List */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-sm border border-white/30 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading zones...</div>
        ) : zones.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <div className="text-4xl mb-2">📦</div>
            <p className="font-medium">No zones created yet</p>
            <p className="text-sm mt-1">Create your first zone to get started</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Icon</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Categories</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Products</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Order</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {zones.map((zone) => (
                    <tr key={zone._id} className="hover:bg-purple-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-2xl">{zone.icon || '📦'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-800">{zone.name}</p>
                          {zone.description && (
                            <p className="text-xs text-gray-500 line-clamp-1">{zone.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(zone.categories || []).slice(0, 3).map((cat) => (
                            <span key={cat} className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-700 font-medium">
                              {cat}
                            </span>
                          ))}
                          {(zone.categories || []).length > 3 && (
                            <span className="px-2 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700 font-medium">
                              +{zone.categories.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="flex items-center gap-1">
                          <Package size={14} />
                          {zone.productCount || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{zone.order}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 text-xs rounded-full font-semibold ${
                          zone.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {zone.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditForm(zone)}
                            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-all"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(zone)}
                            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-all"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden p-3 sm:p-4">
              <div className="space-y-3">
                {zones.map((zone) => (
                  <div key={zone._id} className="bg-white/90 rounded-2xl border border-gray-100 shadow-sm p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{zone.icon || '📦'}</span>
                        <div>
                          <p className="text-base font-bold text-gray-900">{zone.name}</p>
                          {zone.description && (
                            <p className="text-xs text-gray-500 line-clamp-1">{zone.description}</p>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full font-semibold shrink-0 ${
                        zone.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {zone.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="mt-3 flex items-center gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {zone.productCount || 0} products
                      </span>
                      <span>Order: {zone.order}</span>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-1">
                      {(zone.categories || []).slice(0, 4).map((cat) => (
                        <span key={cat} className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-700 font-medium">
                          {cat}
                        </span>
                      ))}
                      {(zone.categories || []).length > 4 && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700 font-medium">
                          +{zone.categories.length - 4}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => openEditForm(zone)}
                        className="flex-1 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 font-semibold text-sm hover:bg-blue-100 flex items-center justify-center gap-1.5"
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(zone)}
                        className="flex-1 px-3 py-2 rounded-xl bg-red-50 text-red-700 font-semibold text-sm hover:bg-red-100 flex items-center justify-center gap-1.5"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminZones;

